import { useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { getBookingDateKeyAndLabel } from './bookingTypeUtils';
import './pagination.css';
import './bookingactions.css';
import './bookingstable.css';

// Helper function for date comparison
const CompareDates = (aValue, bValue) => {
    const dateA = new Date(aValue);
    const dateB = new Date(bValue);
    const isDateAInvalid = Number.isNaN(dateA.getTime());
    const isDateBInvalid = Number.isNaN(dateB.getTime());
    if (isDateAInvalid && isDateBInvalid) {
        return 0;
    }
    if (isDateAInvalid) {
        return 1;
    }
    if (isDateBInvalid) {
        return -1;
    }
    return dateA.getTime() - dateB.getTime();
}

const SortableTable = ({
    data,
    operators,
    bookingType,
    onDataChange,
    loading = false,
    showQuickSort = true,
    showOperatorColumn = true
}) => {
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'ascending'
    });

    // Memoize the sorting function to prevent infinite loops
    const sortedData = useMemo(() => {
        if (!sortConfig.key || !data) return data;

        return [...data].sort((a, b) => {
            const aValue = a[sortConfig.key];
            const bValue = b[sortConfig.key];

            // Handle null/undefined values
            if (aValue === null || aValue === undefined) return 1;
            if (bValue === null || bValue === undefined) return -1;

            let comparison = 0;

            // Handle different data types
            if (sortConfig.key === 'bookingId') {
                // BookingId - string/number comparison
                comparison = String(aValue).localeCompare(String(bValue), undefined, { numeric: true });
            } else {
                const { dateKey } = getBookingDateKeyAndLabel(bookingType);
                if ([dateKey, 'expiryDate', 'processingFailed'].includes(sortConfig.key)) {
                    // DateTime comparison using helper
                    comparison = CompareDates(aValue, bValue);
                } else {
                    // Generic string comparison
                    comparison = String(aValue).localeCompare(String(bValue));
                }
            }

            return sortConfig.direction === 'ascending' ? comparison : -comparison;
        });
    }, [data, sortConfig]);

    // Use useEffect to notify parent of changes (not useMemo)
    useEffect(() => {
        if (onDataChange) {
            onDataChange(sortedData);
        }
    }, [sortedData, onDataChange]);

    // Use useEffect for side effects (not useMemo)
    useEffect(() => {
        setSortConfig({ key: null, direction: 'ascending' });
    }, [bookingType]);

    // Stable sort handler
    const handleSort = useCallback((key) => {
        setSortConfig(prevConfig => {
            let direction = 'ascending';
            if (prevConfig.key === key && prevConfig.direction === 'ascending') {
                direction = 'descending';
            }
            return { key, direction };
        });
    }, []);

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return ' ↕️';
        }
        return sortConfig.direction === 'ascending' ? ' ⬆️' : ' ⬇️';
    };

    const clearSort = useCallback(() => {
        setSortConfig({ key: null, direction: 'ascending' });
    }, []);

    const formatProcessingFailed = useCallback((processingFailedDate) => {
        if (!processingFailedDate) {
            return <span style={{ color: '#2e7d32' }}>No</span>;
        }

        return (
            <span style={{ color: '#d32f2f' }}>
                {processingFailedDate}
            </span>
        );
    }, []);

    // Use bookingTypeUtils for dateKey and dateLabel
    const { dateKey, dateLabel } = getBookingDateKeyAndLabel(bookingType);

    return (
        <div>
            {/* Sort controls */}
            {sortConfig.key && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                    <span style={{ fontSize: '0.9rem', color: '#666' }}>
                        Sorted by: <strong>{sortConfig.key}</strong> ({sortConfig.direction})
                    </span>
                    <button
                        className="themed-button"
                        onClick={clearSort}
                        style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}
                    >
                        Clear Sort
                    </button>
                </div>
            )}

            {/* Table */}
            {loading ? (
                <div className="spinner" />
            ) : (
                <table className="themed-table sortable-table">
                    <thead>
                        <tr>
                            <th
                                className={`sortable-header ${sortConfig.key === 'bookingId' ? 'active' : ''}`}
                                onClick={() => handleSort('bookingId')}
                                style={{
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    minWidth: '80px', //Fixed width for Booking ID 
                                    width: showOperatorColumn ? '15%' : '20%'
                                }}
                                title="Click to sort by Booking ID"
                            >
                                BookingId{getSortIcon('bookingId')}
                            </th>

                            <th
                                className={`sortable-header ${sortConfig.key === dateKey ? 'active' : ''}`}
                                onClick={() => handleSort(dateKey)}
                                style={{
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    minWidth: '180px', // Fixed width for Created/Updated Date
                                    width: showOperatorColumn ? '25%' : '30%'
                                }}
                                title={`Click to sort by ${dateLabel} date`}
                            >
                                {dateLabel}
                                {getSortIcon(dateKey)}
                            </th>

                            {/* Conditionally render Operator column */}
                            {showOperatorColumn && (
                                <th
                                    className={`sortable-header ${sortConfig.key === 'operatorName' ? 'active' : ''}`}
                                    onClick={() => handleSort('operatorName')}
                                    style={{
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        minWidth: '150px', // Fixed width for Operator
                                        width: '20%'
                                    }}
                                    title="Click to sort by Operator Name"
                                >
                                    Operator{getSortIcon('operatorName')}
                                </th>
                            )}

                            <th
                                className={`sortable-header ${sortConfig.key === 'processingFailed' ? 'active' : ''}`}
                                onClick={() => handleSort('processingFailed')}
                                style={{
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    minWidth: '150px', // Fixed width for Processing Failed
                                    width: showOperatorColumn ? '20%' : '25%'
                                }}
                                title="Click to sort by Processing Failed status"
                            >
                                Processing Failed{getSortIcon('processingFailed')}
                            </th>

                            <th
                                className={`sortable-header ${sortConfig.key === 'expiryDate' ? 'active' : ''}`}
                                onClick={() => handleSort('expiryDate')}
                                style={{
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    minWidth: '140px', // Fixed width for Expiry Date
                                    width: showOperatorColumn ? '20%' : '25%'
                                }}
                                title="Click to sort by Expiry Date"
                            >
                                Expired{getSortIcon('expiryDate')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedData?.map(b => {
                            const operatorName = operators ? (operators.find(op => op.operatorId === b.operatorId)?.operatorName || '') : '';
                            return (
                                <tr key={b.bookingId}>
                                    <td>{b.bookingId}</td>
                                    <td>{b[dateKey]}</td>

                                    {showOperatorColumn && (

                                        <td style={{ minWidth: '150px' }}>{operatorName}</td>
                                    )}
                                    <td style={{ minWidth: '140px' }}>{formatProcessingFailed(b.processingFailed)}</td>
                                    <td style={{ minWidth: '140px' }}>{b.expiryDate}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}

            {/* Quick sort shortcuts */}
            {showQuickSort && (
                <div style={{
                    marginTop: '1rem',
                    display: 'flex',
                    gap: '0.5rem',
                    fontSize: '0.8rem',
                    justifyContent: 'flex-end',
                    flexWrap: 'wrap'

                }}>
                    <button
                        className="themed-button"
                        onClick={() => handleSort('bookingId')}
                        style={{ padding: '0.2rem 0.5rem', fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                    >
                        Sort by ID
                    </button>
                    <button
                        className="themed-button"
                        onClick={() => handleSort(dateKey)}
                        style={{ padding: '0.2rem 0.5rem', fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                    >
                        Sort by Date
                    </button>
                    <button
                        className="themed-button"
                        onClick={() => handleSort('processingFailed')}
                        style={{ padding: '0.2rem 0.5rem', fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                    >
                        Sort by Failed
                    </button>
                </div>
            )}
        </div>
    );
};

SortableTable.propTypes = {
    data: PropTypes.array,
    operators: PropTypes.array,
    bookingType: PropTypes.string,
    onDataChange: PropTypes.func,
    loading: PropTypes.bool,
    showQuickSort: PropTypes.bool,
    showOperatorColumn: PropTypes.bool,
};

export default SortableTable;